import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Receipt, ShoppingCart, Trash, Eye
} from '@phosphor-icons/react';
import { toast } from 'sonner';

import { API } from '../config/api';

const statusColors = {
  ricevuta: 'bg-blue-100 text-blue-700 border-blue-200',
  registrata: 'bg-green-100 text-green-700 border-green-200',
};

export default function PassiveInvoicesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/passive-invoices`);
      setList(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Error fetching passive invoices:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRegisterAsPurchase = async (item) => {
    if (!window.confirm(t('passiveInvoice.registerConfirm'))) return;
    try {
      await axios.post(`${API}/passive-invoices/${item.id}/register-purchase`);
      toast.success(t('passiveInvoice.registered'));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('passiveInvoice.registerError'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('passiveInvoice.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/passive-invoices/${id}`);
      toast.success(t('passiveInvoice.deleted'));
      fetchData();
    } catch (err) {
      toast.error(t('passiveInvoice.deleteError'));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const parts = dateStr.split('-');
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    } catch (e) {}
    return dateStr;
  };

  const formatCurrency = (val) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val || 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0A0F1A] flex items-center gap-2" data-testid="passive-invoices-title">
          <Receipt size={28} weight="duotone" className="text-[#0033A0]" />
          {t('passiveInvoice.title')}
        </h1>
        <p className="text-[#4A5568] mt-1">{t('passiveInvoice.subtitle')}</p>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#4A5568]">{t('common.loading')}</div>
      ) : (list || []).length === 0 ? (
        <Card className="border-[#E2E8F0]">
          <CardContent className="text-center py-16">
            <Receipt size={48} className="mx-auto text-[#CBD5E0] mb-4" weight="duotone" />
            <h3 className="font-medium text-[#0A0F1A] mb-2">{t('passiveInvoice.noInvoices')}</h3>
            <p className="text-sm text-[#4A5568]">{t('passiveInvoice.noInvoicesHint')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {(list || []).map((item) => (
            <Card key={item.id} className="border-[#E2E8F0] hover:border-[#CBD5E0] transition-colors" data-testid={`passive-invoice-card-${item.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <span className="font-outfit font-bold text-[#0033A0]">
                        {item.invoice_number || item.sdi_id?.substring(0, 12) || 'N/D'}
                      </span>
                      <Badge className={`text-[10px] ${statusColors[item.status] || 'bg-gray-100 text-gray-600'}`}>
                        {t(`passiveInvoice.statusLabels.${item.status}`) || item.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-[#0A0F1A] font-medium">{item.supplier_name || 'Fornitore sconosciuto'}</p>
                    <p className="text-xs text-[#4A5568]">
                      {formatDate(item.invoice_date)} | P.IVA: {item.supplier_vat || '-'} | {formatCurrency(item.total)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!item.registered_as_purchase && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRegisterAsPurchase(item)}
                        className="text-green-700 border-green-300 hover:bg-green-50 text-xs"
                        data-testid={`passive-register-${item.id}`}
                      >
                        <ShoppingCart size={14} className="mr-1" />
                        {t('passiveInvoice.registerAsPurchase')}
                      </Button>
                    )}
                    {item.purchase_id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/acquisti/${item.purchase_id}/edit`)}
                        data-testid={`passive-view-purchase-${item.id}`}
                      >
                        <Eye size={18} className="text-[#4A5568]" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} data-testid={`passive-delete-${item.id}`}>
                      <Trash size={18} className="text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

